import React, { useState } from "react";
import { Modal, Form, Input, Checkbox, Button, Typography, Descriptions, Card } from "antd";
import { useCustomMutation } from "@refinedev/core";
import { PROCUREMENT_PLAN_APPROVE_MUTATION } from "../queries";

const { Text } = Typography;

interface ApprovalDialogProps {
  visible: boolean;
  onClose: () => void;
  plan: any; // IProcurementPlan type
  onApprovalSuccess: () => void;
}

export const ApprovalDialog: React.FC<ApprovalDialogProps> = ({
  visible,
  onClose,
  plan,
  onApprovalSuccess,
}) => {
  const [form] = Form.useForm();
  const [checked, setChecked] = useState(false);
  
  const { mutate: approvePlan, isLoading } = useCustomMutation();

  const handleApprove = async (values: any) => {
    if (!checked) {
      // Show error if checkbox is not checked
      form.setFields([
        {
          name: "confirmation",
          errors: ["Please confirm that you approve this procurement plan"],
        },
      ]);
      return;
    }

    try {
      approvePlan({
        meta: {
          gqlMutation: PROCUREMENT_PLAN_APPROVE_MUTATION,
        },
        values: {
          planId: plan.id,
          remarks: values.remarks,
        },
        url: "",
        method: "post"
      }, {
        onSuccess: () => {
          onApprovalSuccess();
          onClose();
          form.resetFields();
        },
      });
    } catch (error) {
      console.error("Error approving plan:", error);
    }
  };

  const handleCancel = () => {
    onClose();
    form.resetFields();
  };

  return (
    <Modal
      title="Approve Procurement Plan"
      visible={visible}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          Cancel
        </Button>,
        <Button
          key="approve"
          type="primary"
          onClick={() => form.submit()}
          loading={isLoading}
          disabled={!checked}
        >
          Approve Plan
        </Button>,
      ]}
    >
      <Form form={form} layout="vertical" onFinish={handleApprove}>
        <Card title="Plan Summary" size="small" style={{ marginBottom: 16 }}>
          <Descriptions column={2} size="small" bordered>
            <Descriptions.Item label="Plan Number">
              {plan.planNumber}
            </Descriptions.Item>
            <Descriptions.Item label="Title">
              {plan.title}
            </Descriptions.Item>
            <Descriptions.Item label="Team">
              {plan.teamName}
            </Descriptions.Item>
            <Descriptions.Item label="Fiscal Year">
              {plan.fiscalYear}
            </Descriptions.Item>
            <Descriptions.Item label="Status">
              {plan.status}
            </Descriptions.Item>
            <Descriptions.Item label="Total Amount">
              <Text strong>ETB {plan.totalAmount.toLocaleString()}</Text>
            </Descriptions.Item>
          </Descriptions>
        </Card>
        
        <Form.Item
          name="confirmation"
          valuePropName="checked"
          rules={[
            {
              validator: (_) =>
                checked
                  ? Promise.resolve()
                  : Promise.reject(new Error("Please confirm that you approve this procurement plan")),
            },
          ]}
        >
          <Checkbox
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          >
            I approve this procurement plan
          </Checkbox>
        </Form.Item>
        
        <Form.Item
          label="Remarks (Optional)"
          name="remarks"
        >
          <Input.TextArea
            placeholder="Enter any additional remarks about the approval"
            rows={4}
          />
        </Form.Item>
      </Form>
    </Modal>
  );
};